/**
 * Popsie Fish Co — Tier Picker Integration
 * Bridges custom tier cards with Dawn's native variant-selects pipeline.
 * Uses event delegation so clicks survive Dawn's section re-rendering.
 *
 * Flow: click tier card → update hidden <select> → dispatch change →
 * Dawn fetches new section HTML → price/media/availability update.
 *
 * Accessibility: ARIA radiogroup with roving tabindex, keyboard navigation
 * (Arrow keys, Enter, Space), and prefers-reduced-motion support.
 */
(function () {
  'use strict';

  /**
   * Select a tier card: update visual state, ARIA attributes, hidden
   * <select>, and trigger Dawn's variant pipeline.
   * @param {Element} card - The .popsie-tier-card to activate
   * @param {Object} [opts] - Options
   * @param {boolean} [opts.focus] - Whether to move focus to the card
   */
  function selectTierCard(card, opts) {
    opts = opts || {};

    if (!card) return;
    if (card.classList.contains('sold-out')) return;

    var picker = card.closest('.popsie-tier-picker');
    if (!picker) return;

    var sectionId = picker.dataset.sectionId;
    var cards = picker.querySelectorAll('.popsie-tier-card');

    // Re-query the hidden select each time (it may have been replaced by Dawn)
    var hiddenSelect = document.querySelector(
      '#variant-selects-' + sectionId + ' select'
    );
    if (!hiddenSelect) return;

    // Update visual state and ARIA attributes (roving tabindex)
    cards.forEach(function (c) {
      c.classList.remove('active');
      c.setAttribute('aria-checked', 'false');
      c.setAttribute('tabindex', '-1');
    });
    card.classList.add('active');
    card.setAttribute('aria-checked', 'true');
    card.setAttribute('tabindex', '0');

    // Move focus if requested (keyboard navigation)
    if (opts.focus) {
      card.focus();
    }

    // Check the hidden radio
    var radio = card.querySelector('input[type="radio"]');
    if (radio) radio.checked = true;

    // Try matching by option-value-id first (most reliable)
    var optionValueId = card.dataset.optionValueId;
    var matched = false;

    if (optionValueId) {
      for (var i = 0; i < hiddenSelect.options.length; i++) {
        var opt = hiddenSelect.options[i];
        if (opt.dataset.optionValueId === optionValueId) {
          hiddenSelect.selectedIndex = i;
          matched = true;
          break;
        }
      }
    }

    // Fallback: match by variant title text
    if (!matched) {
      var weightEl = card.querySelector('.popsie-tier-weight');
      if (!weightEl) return;
      var weightText = weightEl.textContent.trim();

      for (var j = 0; j < hiddenSelect.options.length; j++) {
        var optText = hiddenSelect.options[j].textContent.trim();
        if (optText === weightText || optText.indexOf(weightText) !== -1 ||
            optText.replace(/lbs/g, 'lb') === weightText) {
          hiddenSelect.selectedIndex = j;
          matched = true;
          break;
        }
      }
    }

    // Trigger Dawn's native change event to kick off the variant pipeline
    if (matched) {
      hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // --- Click handler (event delegation — survives Dawn's AJAX section re-renders) ---
  document.addEventListener('click', function (e) {
    var card = e.target.closest('.popsie-tier-card');
    if (!card) return;

    selectTierCard(card);
  });

  // --- Keyboard handler (roving tabindex pattern for radiogroup) ---
  document.addEventListener('keydown', function (e) {
    var card = e.target.closest('.popsie-tier-card');
    if (!card) return;

    var picker = card.closest('.popsie-tier-picker');
    if (!picker) return;

    var key = e.key;
    var isArrow = (key === 'ArrowLeft' || key === 'ArrowUp' ||
                   key === 'ArrowRight' || key === 'ArrowDown');
    var isActivate = (key === 'Enter' || key === ' ');

    if (!isArrow && !isActivate) return;

    // Prevent page scrolling on arrow keys and space
    e.preventDefault();

    if (isActivate) {
      // Enter/Space: confirm selection (same as click)
      selectTierCard(card, { focus: true });
      return;
    }

    // Arrow keys: move to previous/next available (non-sold-out) card
    var cards = Array.prototype.slice.call(
      picker.querySelectorAll('.popsie-tier-card')
    );
    var currentIndex = cards.indexOf(card);
    if (currentIndex === -1) return;

    var direction = (key === 'ArrowRight' || key === 'ArrowDown') ? 1 : -1;
    var len = cards.length;
    var nextIndex = currentIndex;

    // Walk in direction, wrapping around, skipping sold-out cards
    for (var step = 0; step < len; step++) {
      nextIndex = (nextIndex + direction + len) % len;
      if (!cards[nextIndex].classList.contains('sold-out')) {
        selectTierCard(cards[nextIndex], { focus: true });
        return;
      }
    }
  });
})();
